import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import {
  PrimaryButtonLargeV2,
  SecondaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import { SearchBar } from "../../../../../../components/SearchBar/SearchBar";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { Table } from "../../../../../../components/Table/Table";
import { endpoints } from "../../../../../../endpoints";
import type {
  EntityType,
  SubsidiaryCandidatesResponse,
} from "../../../../../../types/types.MDM";
import { useDebounce } from "../../../../../../util/hooks";
import { fetcher, TablePlaceholder } from "../../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import axios from "axios";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const InstructionText = styled.p`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  margin: 0 0 ${({ theme }) => theme.resolve("spacing_sm")};
`;

const SearchWrapper = styled.div`
  width: 100%;
`;

const CountLabel = styled.span`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
  display: block;
`;

const TableWrapper = styled.div`
  flex: 1;
  min-height: 200px;
  overflow: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.resolve("spacing_xl")};
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  button {
    flex: 1;
  }
`;

type SubsidiaryRow = {
  id: string;
  name: string;
  [key: string]: string;
};

type AddSubsidiaryModalProps = {
  hierarchyEndpoint: string;
  show: boolean;
  entityType: EntityType;
  storefrontId: string;
  currentEntityId: string;
  onAdd: () => void;
  onCancel: () => void;
};

export function AddSubsidiaryModal({
  show,
  entityType,
  storefrontId,
  currentEntityId,
  hierarchyEndpoint,
  onAdd,
  onCancel,
}: AddSubsidiaryModalProps): JSX.Element {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 400);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const endpoint = endpoints.v2_storefronts_entity_subsidiary_candidates(
    storefrontId,
    currentEntityId
  );
  const swrKey = useMemo(
    () =>
      `${endpoint}?limit=100&offset=0${
        debouncedSearchQuery
          ? `&q=${encodeURIComponent(debouncedSearchQuery)}`
          : ""
      }`,
    [endpoint, debouncedSearchQuery]
  );

  const {
    data,
    isValidating,
    mutate: mutateCandidates,
  } = useSWR<SubsidiaryCandidatesResponse>(show ? swrKey : null, fetcher, {
    revalidateOnFocus: false,
  });

  // Preview columns come from the candidates response so they always match the
  // attribute values returned for each row. "Company Name" is rendered as the
  // dedicated name column, so it's excluded from the attribute columns.
  const attributeDefinitions = useMemo(
    () =>
      data?.subsidiary_preview_attributes?.filter(
        (attr) => attr.name !== "Company Name"
      ) ?? [],
    [data?.subsidiary_preview_attributes]
  );

  const { getLabel } = useMdmEntityDisplayNames();
  const entityLabel = getLabel(entityType, "singular");
  const entityLabelPlural = getLabel(entityType, "plural");

  useEffect(() => {
    if (!show) {
      setIsLoading(false);
      setSelectedIds([]);
      setSearchQuery("");
    }
  }, [show]);

  // The backend already excludes the current entity, its existing
  // subsidiaries, and its current parent from the candidate list.
  const tableData: SubsidiaryRow[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((e) => {
      const row: SubsidiaryRow = { id: e.id, name: e.name };
      for (const a of attributeDefinitions) {
        row[a.name] =
          e.attributes?.find((b) => a.name === b.name)?.value ?? "--";
      }
      return row;
    });
  }, [data?.data, attributeDefinitions]);

  const columns = useMemo(
    () => [
      {
        header: t("{{entity}} Name", { entity: entityLabel }),
        accessorKey: "name",
        id: "name",
      },
      ...(attributeDefinitions?.map((attribute) => ({
        header: attribute.display_name,
        accessorKey: attribute.name,
        id: attribute.name,
      })) ?? []),
    ],
    [entityLabel, t, attributeDefinitions]
  );

  const handleSave = async () => {
    const selected = tableData.filter((row) => selectedIds.includes(row.id));
    setIsLoading(true);
    try {
      await axios.post(hierarchyEndpoint!, {
        children_ids: selected.map((s) => s.id),
      });
      notifySuccess(t("Subsidiaries added successfully."));
      setSelectedIds([]);
      // Refresh candidates so the just-added entities (now excluded
      // server-side) don't reappear when the modal is reopened.
      void mutateCandidates();
      onAdd();
    } catch (error) {
      notifyError(
        (error as any)?.response?.data?.message ??
          t("There was an error adding the subsidiaries."),
        { error }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRows = (rows: string[] | "all") => {
    setSelectedIds(rows === "all" ? tableData.map((r) => r.id) : rows);
  };

  return (
    <SlideOut show={show} closeFlyout={onCancel} title={t("Add Subsidiaries")}>
      <ContentWrapper>
        <InstructionText>{t("Choose Company")}</InstructionText>

        <SearchWrapper>
          <SearchBar
            placeHolder={t("Search by {{entity}} Name or ID", {
              entity: entityLabel,
            })}
            query={searchQuery}
            handleChange={(e) => setSearchQuery(e.target.value)}
            handleClearInput={() => setSearchQuery("")}
          />
        </SearchWrapper>

        <CountLabel>
          {t("Showing {{count}} {{entities}}", {
            count: tableData.length,
            entities: entityLabelPlural.toLowerCase(),
          })}
        </CountLabel>

        <TableWrapper>
          <Table
            columns={columns}
            data={tableData}
            isLoading={isValidating && !data}
            error={undefined}
            handleSelectRows={handleSelectRows}
            defaultSelectedRows={selectedIds}
            rowSelectionType="checkbox"
            Placeholder={<TablePlaceholder message={t("No items to show.")} />}
            tableHeight="500px"
            lastChildleftAlign={true}
          />
        </TableWrapper>

        <ButtonContainer>
          <SecondaryButtonMediumV2 type="button" onClick={onCancel}>
            {t("Cancel")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonLargeV2
            type="button"
            onClick={handleSave}
            disabled={selectedIds.length === 0 || isLoading}
            loading={isLoading}
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </ContentWrapper>
    </SlideOut>
  );
}
